import React, { useCallback, useEffect, useState } from 'react'
import {
  // StyleTopLine,
  StyledBottomLine,
  StyledContinueButtonContainer,
  // StyledDatePicker,
  StyledHouseParametersCol,
  StyledHouseParametersColConstruction,
  StyledHouseParametersColConstructionYear,
  StyledHouseParametersColElevator,
  StyledHouseParametersColLivingArea,
  StyledHouseParametersColPlotArea,
  StyledHouseParametersRowContainer,
  StyledLine,
  StyledObjectData,
  StyledObjectType,
} from './MapOrReportObjectDataForm.styled'
import RadioGroupButtons from 'components/RadioGroupButtons/RadioGroupButtons'
import { useAppDispatch } from 'store/hooks'
import { useSelector } from 'react-redux'
import {
  removeFormField,
  selectAddressFormData,
  setEquipmentDetailsChanged,
  setFormField,
} from 'slices/addressFormSlice'
import { useTranslation } from 'react-i18next'
import { HouseApartmentType, ObjectType } from 'types/ApiResponse'
import { getObjectTypeOptions } from '../../ObjectTypeOptions/ObjectTypeOptions'
import {
  elevatorSelect,
  handleNumberInput,
  hausTypeEFHandMFH,
  hausTypeETW,
  hausTypeETW_AT,
} from '../../helper'
import Button from 'components/Button/Button'
import AddImageUpload from './AddImageUpload/AddImageUpload'
import MultiSelect from 'components/MultiSelect/MultiSelect'
import Input from 'components/Input/Input'
import { InputAdornment } from '@material-ui/core'
// import { DatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers'
// import i18n from 'common/utils/i18n/i18n'
// import MomentUtils from '@date-io/moment'
// import moment from 'moment'

const MapOrReportObjectDataForm = (props: any) => {
  const { setCurrentTab } = props
  const dispatch = useAppDispatch()
  const addressFormData = useSelector(selectAddressFormData)
  const { t } = useTranslation()
  const [showValidation, setShowValidation] = useState(false)
  const [validationErrors, setValidationErrors] = useState([])
  const isDE = addressFormData.addressData?.nation === 'DE'
  const objectTypeOptions = getObjectTypeOptions(isDE, t)
  const objectType = addressFormData.addressData?.propertyDetails?.objectType
  // const dateFormat = 'YYYY-MM-DD'
  const numberRegex = /^\d*(,\d{0,2})?$/

  const validateNumericField = (currentValue, min, max, key, isFloat = false) => {
    const numericValue = isFloat
      ? parseFloat(currentValue.replace(',', '.'))
      : parseInt(currentValue)
    const numericMin = parseInt(min)
    const numericMax = parseInt(max)
    if (numericValue >= numericMax) {
      dispatch(setFormField({ key, value: `${numericMax}` }))
    } else if (numericValue <= numericMin) {
      dispatch(setFormField({ key, value: `${numericMin}` }))
    }
  }

  // const handleChangeValuationDate = useCallback(
  //   (valuationDate: any) => {
  //     dispatch(setFormField({ key: 'valuationDate', value: valuationDate.format(dateFormat) }))
  //   },
  //   [dispatch]
  // )

  const handleChangeObjectType = useCallback(
    (value: ObjectType) => {
      dispatch(setFormField({ key: 'propertyDetails.objectType', value }))
      dispatch(setEquipmentDetailsChanged(true))
      setShowValidation(false)
      setValidationErrors([])
    },
    [dispatch]
  )

  const onChangeHouseType = (houseOrApartmentType: HouseApartmentType) => {
    dispatch(
      setFormField({ key: 'propertyDetails.houseOrApartmentType', value: houseOrApartmentType })
    )
    dispatch(setEquipmentDetailsChanged(true))
  }

  const onChangeBuildYear = (constructionYear: string, validate: boolean = false) => {
    dispatch(setEquipmentDetailsChanged(true))
    if (constructionYear.length === 0) {
      dispatch(removeFormField('propertyDetails.constructionYear'))
      return
    }
    dispatch(setFormField({ key: 'propertyDetails.constructionYear', value: constructionYear }))
    if (validate) {
      validateNumericField(
        constructionYear,
        1800,
        new Date().getFullYear(),
        'propertyDetails.constructionYear'
      )
    }
  }

  const onChangeLivingArea = (livingArea: string, validate: boolean = false) => {
    dispatch(setEquipmentDetailsChanged(true))
    if (livingArea.length === 0) {
      dispatch(removeFormField('propertyDetails.livingArea'))
      return
    }
    if (numberRegex.test(livingArea)) {
      dispatch(setFormField({ key: 'propertyDetails.livingArea', value: livingArea }))
      if (validate) {
        validateNumericField(
          livingArea,
          1,
          objectType === ObjectType.MULTI_FAMILY_HOUSE ? 1000 : 500,
          'propertyDetails.livingArea',
          true
        )
      }
    }
  }

  const onChangePlotArea = (plotArea: string, validate: boolean = false) => {
    dispatch(setEquipmentDetailsChanged(true))
    if (plotArea.length === 0) {
      dispatch(removeFormField('propertyDetails.plotArea'))
      return
    }
    if (numberRegex.test(plotArea)) {
      dispatch(setFormField({ key: 'propertyDetails.plotArea', value: plotArea }))
      if (validate) {
        validateNumericField(plotArea, 1, 5000, 'propertyDetails.plotArea', true)
      }
    }
  }

  const onChangeNumberOfFloors = (numberOfFloors: string, validate: boolean = false) => {
    dispatch(setEquipmentDetailsChanged(true))
    if (numberOfFloors.length === 0) {
      dispatch(removeFormField('propertyDetails.numberOfFloors'))
      return
    }
    dispatch(setFormField({ key: 'propertyDetails.numberOfFloors', value: numberOfFloors }))
    if (validate) {
      validateNumericField(numberOfFloors, 1, 1000, 'propertyDetails.numberOfFloors')
    }
  }

  const onChangeFloors = (floor: string, validate: boolean = false) => {
    dispatch(setEquipmentDetailsChanged(true))
    if (floor.length === 0) {
      dispatch(removeFormField('propertyDetails.floorNumber'))
      return
    }
    dispatch(setFormField({ key: 'propertyDetails.floorNumber', value: floor }))
    if (validate) {
      validateNumericField(floor, 0, 100, 'propertyDetails.floorNumber')
    }
  }

  const onChangeGarages = (garages: string, validate: boolean = false) => {
    dispatch(setEquipmentDetailsChanged(true))
    if (garages.length === 0) {
      dispatch(removeFormField('propertyDetails.garages'))
      return
    }
    dispatch(setFormField({ key: 'propertyDetails.garages', value: garages }))
    if (validate) {
      if (objectType === ObjectType.HOUSE) {
        validateNumericField(garages, 0, 4, 'propertyDetails.garages')
      } else {
        validateNumericField(garages, 0, 1000000, 'propertyDetails.garages')
      }
    }
  }

  const onChangeParkingPlace = (outdoorParkingSpaces: string, validate: boolean = false) => {
    dispatch(setEquipmentDetailsChanged(true))
    if (outdoorParkingSpaces.length === 0) {
      dispatch(removeFormField('propertyDetails.garages'))
      return
    }
    dispatch(
      setFormField({ key: 'propertyDetails.outdoorParkingSpaces', value: outdoorParkingSpaces })
    )
    if (validate) {
      validateNumericField(outdoorParkingSpaces, 0, 1000000, 'propertyDetails.outdoorParkingSpaces')
    }
  }

  const onChangeElevator = (elevator: boolean) => {
    dispatch(setEquipmentDetailsChanged(true))
    dispatch(setFormField({ key: 'propertyDetails.elevator', value: elevator }))
  }

  const onContinueClick = () => {
    setShowValidation(true)
    if (validationErrors.length === 0) {
      setCurrentTab((tab) => tab + 1)
    }
  }

  const validate = (name, property) => {
    if (property === null || property === undefined || property === '') {
      if (!validationErrors.includes(name)) {
        setValidationErrors((errors) => [name, ...errors])
      }
      return showValidation && true
    } else {
      if (validationErrors.includes(name)) {
        setValidationErrors((errors) => errors.filter((error) => error !== name))
      }
      return false
    }
  }

  const validationMultiSelect = (value, options, formFieldName) => {
    if (!options) {
      return value
    }
    const matchingOption = options.find(
      (option) => option.value === `${value}` || option.key === `${value}`
    )
    if (
      (value !== null && value !== undefined && !matchingOption) ||
      value === null ||
      value === undefined
    ) {
      dispatch(removeFormField(formFieldName))
      return false
    }
    return true
  }

  useEffect(() => {
    if (!addressFormData.addressData?.propertyDetails?.objectType) {
      handleChangeObjectType(ObjectType.HOUSE)
    }
    // if (!addressFormData.addressData?.valuationDate) {
    //   handleChangeValuationDate(moment())
    // }
  }, [addressFormData.addressData, handleChangeObjectType])

  return (
    <>
      <StyledObjectData>
        {/* <MuiPickersUtilsProvider locale={i18n.language} utils={MomentUtils}>
          <StyledDatePicker
            validationError={moment(addressFormData.addressData?.valuationDate).isBefore(
              '2022-01-01'
            )}
          >
            <DatePicker
              variant="inline"
              inputVariant="outlined"
              label={`${t('newAddressModal.valuationDate')}*`}
              value={moment(addressFormData.addressData?.valuationDate).format(dateFormat)}
              onChange={handleChangeValuationDate}
              helperText={`01.01.2022 – ${t('common.today')}`}
              format={'DD.MM.YYYY'}
              minDate="2022-01-01"
              maxDate={moment().format(dateFormat)}
            />
          </StyledDatePicker>
        </MuiPickersUtilsProvider>
        <StyleTopLine /> */}
        <StyledObjectType>
          <RadioGroupButtons
            options={objectTypeOptions}
            label={`${t('newAddressModal.objectDataTab.objectType')}*`}
            onChangeProps={handleChangeObjectType}
            value={addressFormData.addressData?.propertyDetails?.objectType}
          />
        </StyledObjectType>
        <StyledLine />
        <StyledHouseParametersRowContainer>
          {addressFormData.addressData?.propertyDetails?.objectType !==
            ObjectType.MULTI_FAMILY_HOUSE && (
            <StyledHouseParametersCol>
              <StyledHouseParametersColConstruction
                validationError={validate(
                  'houseOrApartmentType',
                  addressFormData.addressData?.propertyDetails?.houseOrApartmentType
                )}
              >
                <MultiSelect
                  value={addressFormData.addressData?.propertyDetails?.houseOrApartmentType || ''}
                  options={
                    objectType === ObjectType.APARTMENT
                      ? isDE
                        ? hausTypeETW
                        : hausTypeETW_AT
                      : hausTypeEFHandMFH
                  }
                  label={`${t('newAddressModal.objectDataTab.houseType')}*`}
                  onChange={(event) => onChangeHouseType(event.target.value)}
                  validation={validationMultiSelect}
                  formFieldName="propertyDetails.houseOrApartmentType"
                />
              </StyledHouseParametersColConstruction>
            </StyledHouseParametersCol>
          )}
          <StyledHouseParametersCol>
            <StyledHouseParametersColConstructionYear
              validationError={validate(
                'constructionYear',
                addressFormData.addressData?.propertyDetails?.constructionYear
              )}
            >
              <Input
                value={addressFormData.addressData?.propertyDetails?.constructionYear}
                onChange={(event) => onChangeBuildYear(event.target.value)}
                onBlur={(event) => onChangeBuildYear(event.target.value, true)}
                onKeyPress={(event) => handleNumberInput(event)}
                label={`${t('newAddressModal.objectDataTab.buildYear')}*`}
                helperText={`1800 – ${t('common.today')}`}
                defaultValue={addressFormData.addressData?.propertyDetails?.constructionYear}
              />
            </StyledHouseParametersColConstructionYear>
          </StyledHouseParametersCol>
          <StyledHouseParametersCol>
            <StyledHouseParametersColLivingArea
              validationError={validate(
                'livingArea',
                addressFormData.addressData?.propertyDetails?.livingArea
              )}
            >
              <Input
                value={addressFormData.addressData?.propertyDetails?.livingArea}
                onChange={(event) => onChangeLivingArea(event.target.value)}
                onBlur={(event) => onChangeLivingArea(event.target.value, true)}
                onKeyPress={(event) => handleNumberInput(event, true)}
                defaultValue={addressFormData.addressData?.propertyDetails?.livingArea}
                label={`${t('newAddressModal.objectDataTab.livingArea')}*`}
                helperText={`max. ${objectType === ObjectType.MULTI_FAMILY_HOUSE ? '1000' : '500'}`}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end" style={{ color: '#92969a' }}>
                      m2
                    </InputAdornment>
                  ),
                }}
              />
            </StyledHouseParametersColLivingArea>
          </StyledHouseParametersCol>
          {objectType === ObjectType.APARTMENT ? null : (
            <StyledHouseParametersCol>
              <StyledHouseParametersColPlotArea
                validationError={validate(
                  'plotArea',
                  addressFormData.addressData?.propertyDetails?.plotArea
                )}
              >
                <Input
                  value={addressFormData.addressData?.propertyDetails?.plotArea}
                  onChange={(event) => onChangePlotArea(event.target.value)}
                  onBlur={(event) => onChangePlotArea(event.target.value, true)}
                  onKeyPress={(event) => handleNumberInput(event, true)}
                  defaultValue={addressFormData.addressData?.propertyDetails?.plotArea}
                  label={`${t('newAddressModal.objectDataTab.plotArea')}*`}
                  helperText="max. 5000"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end" style={{ color: '#92969a' }}>
                        m2
                      </InputAdornment>
                    ),
                  }}
                />
              </StyledHouseParametersColPlotArea>
            </StyledHouseParametersCol>
          )}
          {objectType !== ObjectType.APARTMENT && (
            <StyledHouseParametersCol>
              <Input
                value={addressFormData.addressData?.propertyDetails?.numberOfFloors}
                onChange={(event) => onChangeNumberOfFloors(event.target.value)}
                onBlur={(event) => onChangeNumberOfFloors(event.target.value, true)}
                onKeyPress={(event) => handleNumberInput(event)}
                defaultValue={addressFormData.addressData?.propertyDetails?.numberOfFloors}
                label={`${t('newAddressModal.objectDataTab.numberOfFloors')}`}
                helperText="max. 1000"
              />
            </StyledHouseParametersCol>
          )}
          {objectType === ObjectType.APARTMENT && (
            <StyledHouseParametersCol>
              <Input
                value={addressFormData.addressData?.propertyDetails?.floorNumber}
                onChange={(event) => onChangeFloors(event.target.value)}
                onBlur={(event) => onChangeFloors(event.target.value, true)}
                onKeyPress={(event) => handleNumberInput(event)}
                defaultValue={addressFormData.addressData?.propertyDetails?.floorNumber}
                label={`${t('newAddressModal.objectDataTab.floor')}`}
                helperText="max. 100"
              />
            </StyledHouseParametersCol>
          )}
          {objectType === ObjectType.APARTMENT ? (
            <StyledHouseParametersCol>
              <StyledHouseParametersColElevator>
                <RadioGroupButtons
                  options={elevatorSelect}
                  label={t('newAddressModal.objectDataTab.elevator')}
                  onChange={(event) => onChangeElevator(event.target.value)}
                  value={addressFormData.addressData?.propertyDetails?.elevator}
                />
              </StyledHouseParametersColElevator>
            </StyledHouseParametersCol>
          ) : null}

          <StyledHouseParametersCol>
            <Input
              value={addressFormData.addressData?.propertyDetails?.garages}
              onChange={(event) => onChangeGarages(event.target.value)}
              onBlur={(event) => onChangeGarages(event.target.value, true)}
              onKeyPress={(event) => handleNumberInput(event)}
              defaultValue={addressFormData.addressData?.propertyDetails?.garages}
              label={`${t('newAddressModal.objectDataTab.garage')}`}
              helperText={objectType === ObjectType.HOUSE ? 'max. 4' : null}
            />
          </StyledHouseParametersCol>
          <StyledHouseParametersCol>
            <Input
              value={addressFormData.addressData?.propertyDetails?.outdoorParkingSpaces}
              onChange={(event) => onChangeParkingPlace(event.target.value)}
              onBlur={(event) => onChangeParkingPlace(event.target.value, true)}
              onKeyPress={(event) => handleNumberInput(event)}
              defaultValue={addressFormData.addressData?.propertyDetails?.outdoorParkingSpaces}
              label={`${t('newAddressModal.objectDataTab.parkingPlace')}`}
            />
          </StyledHouseParametersCol>
        </StyledHouseParametersRowContainer>
        <StyledBottomLine />
        {addressFormData.productName === 'report' && <AddImageUpload />}
      </StyledObjectData>
      <StyledContinueButtonContainer>
        <Button kind="primary" onClick={() => onContinueClick()}>
          {t('common.continueButton')}
        </Button>
      </StyledContinueButtonContainer>
    </>
  )
}

export default MapOrReportObjectDataForm
